import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import debounce from 'lodash/debounce'

export const GRANTS_TASKS_DRAFT_STORAGE_KEY = 'grants_tasks_draft'
export const DEFAULT_DRAFT_SAVE_DELAY = 500

// How long the "Saving…" indicator stays visible before flipping to "Saved".
const SAVING_INDICATOR_DELAY = 500

export type DraftSaveStatus = 'idle' | 'saving' | 'saved' | 'error'

export type DraftValues = Record<string, string | number | string[]>

export type StoredTaskDraft<T> = {
  values: T
  savedAt: number
}

export type UseTaskDraftAutosaveParams = {
  candidateId: string
  taskId: string
  enabled: boolean
  delay?: number
}

export type UseTaskDraftAutosaveResult<T> = {
  status: DraftSaveStatus
  lastSavedAt: number | null
  initialDraft: T | null
  scheduleSave: (values: T) => void
  clearDraft: () => void
}

export function buildTaskDraftEntryKey(
  candidateId: string,
  taskId: string
): string {
  return `${candidateId}:${taskId}`
}

function readDraftMap(): Record<string, StoredTaskDraft<unknown>> {
  try {
    const raw = window.localStorage.getItem(GRANTS_TASKS_DRAFT_STORAGE_KEY)

    return raw
      ? (JSON.parse(raw) as Record<string, StoredTaskDraft<unknown>>)
      : {}
  } catch {
    return {}
  }
}

function readDraft<T>(entryKey: string): StoredTaskDraft<T> | null {
  return (readDraftMap()[entryKey] as StoredTaskDraft<T> | undefined) ?? null
}

function writeDraftMap(map: Record<string, StoredTaskDraft<unknown>>): void {
  window.localStorage.setItem(
    GRANTS_TASKS_DRAFT_STORAGE_KEY,
    JSON.stringify(map)
  )
}

export function useTaskDraftAutosave<T extends object>({
  candidateId,
  taskId,
  enabled,
  delay = DEFAULT_DRAFT_SAVE_DELAY,
}: Readonly<UseTaskDraftAutosaveParams>): UseTaskDraftAutosaveResult<T> {
  const entryKey = useMemo(
    () => buildTaskDraftEntryKey(candidateId, taskId),
    [candidateId, taskId]
  )
  const draftRef = useRef<StoredTaskDraft<T> | null>(readDraft<T>(entryKey))
  const savedTimer = useRef<ReturnType<typeof setTimeout>>()

  const [status, setStatus] = useState<DraftSaveStatus>(
    draftRef.current ? 'saved' : 'idle'
  )
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(
    draftRef.current?.savedAt ?? null
  )

  const persist = useCallback(
    (values: T) => {
      try {
        const savedAt = Date.now()

        writeDraftMap({ ...readDraftMap(), [entryKey]: { values, savedAt } })

        setStatus('saving')
        setLastSavedAt(savedAt)
        savedTimer.current = setTimeout(
          () => setStatus('saved'),
          SAVING_INDICATOR_DELAY
        )
      } catch {
        setStatus('error')
      }
    },
    [entryKey]
  )

  const debouncedPersist = useMemo(
    () => debounce(persist, delay),
    [persist, delay]
  )

  useEffect(
    () => () => {
      debouncedPersist.flush()
      clearTimeout(savedTimer.current)
    },
    [debouncedPersist]
  )

  const scheduleSave = useCallback(
    (values: T) => {
      if (!enabled) {
        return
      }

      debouncedPersist(values)
    },
    [enabled, debouncedPersist]
  )

  const clearDraft = useCallback(() => {
    debouncedPersist.cancel()
    clearTimeout(savedTimer.current)

    try {
      const { [entryKey]: _removed, ...rest } = readDraftMap()

      writeDraftMap(rest)
    } catch {
      // ignore
    }

    setStatus('idle')
    setLastSavedAt(null)
  }, [entryKey, debouncedPersist])

  return {
    status,
    lastSavedAt,
    initialDraft: enabled ? draftRef.current?.values ?? null : null,
    scheduleSave,
    clearDraft,
  }
}
